import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Icon, Button, Modal, message, Popover, Badge, Select } from "antd";
import moment from "moment";

import { ModuleHeader, ModuleHeaderWrapper } from "~/components/ModuleHeader";
import { toBRL } from "~/components/UI/InputCurrency";
import { PERMISSIONS } from "~/constants/permissions.constants";
import MonthPicker from "~/_shared/components/MonthPicker";
import Search from "~/_shared/components/Search";
import FilterPillsBar from "~/_shared/components/FilterPillsBar";
import RestoredFiltersNotice from "~/_shared/components/RestoredFiltersNotice";
import usePersistedFilters from "~/_shared/hooks/usePersistedFilters";

import * as contasReceberActions from "./actions";
import TableList from "./Components/TableList";
import DrawerCreate from "./Components/DrawerCreate";
import {
    getDefaultContasReceberFilters,
    getContasReceberPeriodRequest,
    isContasReceberPeriodDefault,
    countActiveContasReceberFilters,
} from "./helpers";

import styled from "styled-components";

var SummaryCard = styled.div`
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 15px;
    text-align: center;
`;

var ContasReceberList = function() {
    var dispatch = useDispatch();
    var data = useSelector(function(state) { return state.contasReceber.data || []; });
    var summary = useSelector(function(state) { return state.contasReceber.summary || {}; });
    var isLoading = useSelector(function(state) { return state.contasReceber.isLoading; });
    var user = useSelector(function(state) { return state.user; });
    var customers = useSelector(function(state) { return state.customers && state.customers.list ? state.customers.list.filter(function(c) { return c.is_customer; }) : []; });

    var [showDrawer, setShowDrawer] = useState(false);
    var [editingRecord, setEditingRecord] = useState(null);

    // Filtros aplicados: sobrevivem ao refresh por alguns minutos (por escritório/usuário/página).
    var persisted = usePersistedFilters('financeiro.contasReceber', getDefaultContasReceberFilters);
    var applied = persisted.filters;
    var setApplied = persisted.setFilters;
    var defaults = persisted.defaults;
    var selectedMonth = applied.month;
    var customRange = applied.customRange;
    var activeView = applied.view;
    var sourceFilter = applied.source;
    var search = applied.search || "";
    var isPeriodDefault = isContasReceberPeriodDefault(applied, defaults);

    var setActiveView = function(view) { setApplied({ view: view }); };
    var setSourceFilter = function(source) { setApplied({ source: source }); };
    var setSearch = function(value) { setApplied({ search: value }); };

    var canEdit = user.permissions.includes(PERMISSIONS.financial.contasReceber.edit);

    // Período aplicado (mês ou personalizado) — usado na carga e nos recarregamentos.
    var periodRequest = getContasReceberPeriodRequest(applied);

    var reloadCurrentPeriod = function() {
        dispatch(contasReceberActions.loadContasReceber(getContasReceberPeriodRequest(applied)));
    };

    // Carrega o período aplicado: ao montar (restaurado ou padrão) e sempre que ele muda,
    // inclusive ao trocar entre mês e período personalizado.
    useEffect(function() {
        dispatch(contasReceberActions.loadContasReceber(periodRequest));
    }, [periodRequest.from, periodRequest.to]);

    var handleMonthChange = function(value) {
        setApplied({ month: value, customRange: null });
    };

    var handleCustomRange = function(from, to) {
        setApplied({ customRange: { from: from, to: to } });
    };

    var handleResetPeriod = function() {
        setApplied({ month: defaults.month, customRange: null });
    };

    var handleMarkAsReceived = function(record) {
        Modal.confirm({
            title: "Marcar como Recebido",
            content: "Confirma o recebimento desta conta?",
            okText: "Sim, confirmar",
            cancelText: "Cancelar",
            onOk: function() {
                dispatch(contasReceberActions.markAsReceived(record.id, moment().format("YYYY-MM-DD")))
                    .then(function() {
                        message.success("Conta marcada como recebida!");
                        reloadCurrentPeriod();
                    });
            },
        });
    };

    var handleDelete = function(record) {
        Modal.confirm({
            title: "Excluir conta a receber?",
            content: "Esta acao nao podera ser desfeita.",
            okText: "Excluir",
            okType: "danger",
            cancelText: "Cancelar",
            onOk: function() {
                dispatch(contasReceberActions.deleteContaReceber(record.id))
                    .then(function() { message.success("Conta excluida!"); })
                    .catch(function(err) {
                        var msg = err && err.response && err.response.data && err.response.data.message;
                        message.error(msg || "Erro ao excluir.");
                    });
            },
        });
    };

    var handleEdit = function(record) {
        setEditingRecord(record);
        setShowDrawer(true);
    };

    var handleSaveSuccess = function() {
        setShowDrawer(false);
        setEditingRecord(null);
        reloadCurrentPeriod();
    };

    var filtered = useMemo(function() {
        var result = data;
        if (sourceFilter !== 'all') {
            result = result.filter(function(r) { return r.source === sourceFilter; });
        }
        if (activeView !== "all") {
            if (activeView === "overdue") {
                result = result.filter(function(r) { return r.status === "pending" && moment(r.due_at).isBefore(moment(), "day"); });
            } else {
                result = result.filter(function(r) { return r.status === activeView; });
            }
        }
        if (search) {
            var s = search.toLowerCase();
            result = result.filter(function(r) {
                return (r.description && r.description.toLowerCase().indexOf(s) > -1) ||
                       (r.notes && r.notes.toLowerCase().indexOf(s) > -1) ||
                       (r.customer && r.customer.name && r.customer.name.toLowerCase().indexOf(s) > -1);
            });
        }
        return result;
    }, [data, activeView, search, sourceFilter]);

    var periodLabel = customRange
        ? moment(customRange.from).format('DD/MM/YY') + ' - ' + moment(customRange.to).format('DD/MM/YY')
        : 'Mes: ' + moment(selectedMonth, 'YYYY-MM').format('MMM/YYYY');
    var pills = [{ key: 'month', label: periodLabel, color: customRange ? '#722ed1' : '#1890ff', icon: 'calendar', onRemove: isPeriodDefault ? undefined : handleResetPeriod }];
    if (activeView !== 'all') pills.push({ key: 'view', label: activeView === 'paid' ? 'Recebidos' : activeView === 'overdue' ? 'Vencidas' : 'Pendentes', color: '#fa8c16', onRemove: function() { setActiveView('all'); } });
    if (sourceFilter !== 'all') {
        var SOURCE_LABELS = { mensalidade: 'Mensalidades', extra: 'Extras', manual: 'Outros' };
        var SOURCE_COLORS = { mensalidade: '#1890ff', extra: '#722ed1', manual: '#13c2c2' };
        pills.push({ key: 'source', label: SOURCE_LABELS[sourceFilter] || sourceFilter, color: SOURCE_COLORS[sourceFilter] || '#595959', onRemove: function() { setSourceFilter('all'); } });
    }
    if (search) pills.push({ key: 'search', label: search, color: '#722ed1', icon: 'search', onRemove: function() { setSearch(''); } });

    var restoredSummary = pills
        .filter(function(pill) { return pill.key !== 'month' || !isPeriodDefault; })
        .map(function(pill) { return pill.label; })
        .join(' · ');

    return (
        <div>
            <ModuleHeaderWrapper>
                <ModuleHeader
                    breadcrumbs={["Financeiro", "Contas a Receber"]}
                    title="Contas a Receber"
                    search={<Search value={search} placeholder="Pesquisar" onChange={function(e) { setSearch(e.target.value); }} />}
                    center={
                        <>
                            <Popover trigger="click" placement="bottomRight" content={
                                <div style={{ width: 380 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                                        <span style={{ fontSize: 15, fontWeight: 600 }}>Filtros</span>
                                        {!persisted.isDefault && <a style={{ fontSize: 12, color: '#f5222d' }} onClick={persisted.resetFilters}>Limpar tudo</a>}
                                    </div>
                                    <div style={{ marginBottom: 18 }}>
                                        <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Período</div>
                                        <MonthPicker value={selectedMonth} onChange={handleMonthChange}
                                            onCustomRange={handleCustomRange}
                                            customRangeLabel={customRange ? moment(customRange.from).format('DD/MM/YY') + ' - ' + moment(customRange.to).format('DD/MM/YY') : null}
                                            showTodayButton={false} style={{ display: 'block', width: '100%' }} />
                                    </div>
                                    <div style={{ marginBottom: 18 }}>
                                        <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {[
                                                { value: 'all', label: 'Todos', color: '#8c8c8c' },
                                                { value: 'pending', label: 'Pendente', color: '#fa8c16' },
                                                { value: 'paid', label: 'Recebido', color: '#52c41a' },
                                                { value: 'overdue', label: 'Vencida', color: '#f5222d' },
                                            ].map(function(opt) {
                                                var isActive = activeView === opt.value;
                                                return (
                                                    <div key={opt.value} onClick={function() { setActiveView(opt.value); }}
                                                        style={{ padding: '6px 14px', borderRadius: 20, cursor: 'pointer', border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'), background: isActive ? opt.color + '10' : '#fff', fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? opt.color : '#595959', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: opt.color }} />
                                                        {opt.label}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div style={{ marginBottom: 18 }}>
                                        <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Origem</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {[
                                                { value: 'all', label: 'Todos', color: '#8c8c8c' },
                                                { value: 'mensalidade', label: 'Mensalidades', color: '#1890ff' },
                                                { value: 'extra', label: 'Extras', color: '#722ed1' },
                                                { value: 'manual', label: 'Outros', color: '#13c2c2' },
                                            ].map(function(opt) {
                                                var isActive = sourceFilter === opt.value;
                                                return (
                                                    <div key={opt.value} onClick={function() { setSourceFilter(opt.value); }}
                                                        style={{ padding: '6px 14px', borderRadius: 20, cursor: 'pointer', border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'), background: isActive ? opt.color + '10' : '#fff', fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? opt.color : '#595959', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: opt.color }} />
                                                        {opt.label}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            }>
                                <Badge count={countActiveContasReceberFilters(applied, defaults)} offset={[-4, 0]} style={{ backgroundColor: '#722ed1' }}>
                                    <Button icon="bars" style={{ borderRadius: 8 }}>Filtros</Button>
                                </Badge>
                            </Popover>
                            <Button icon="plus" shape="circle" onClick={function() { setEditingRecord(null); setShowDrawer(true); }} />
                        </>
                    }
                    advanced={<FilterPillsBar pills={pills} onClearAll={persisted.isDefault ? undefined : persisted.resetFilters} />}
                />
            </ModuleHeaderWrapper>

            <RestoredFiltersNotice
                visible={persisted.wasRestored}
                summary={restoredSummary}
                onReset={persisted.resetFilters}
                onDismiss={persisted.dismissRestored}
            />

            <div style={{ display: 'flex', gap: 12, padding: '10px 0', flexWrap: 'nowrap' }}>
                {[
                    { label: 'Total', value: summary.total, count: summary.count_total, color: '#262626' },
                    { label: 'Pendentes', value: summary.pending, count: summary.count_pending, color: '#fa8c16', view: 'pending' },
                    { label: 'Recebidos', value: summary.paid, count: summary.count_paid, color: '#52c41a', view: 'paid' },
                    { label: 'Vencidas', value: summary.overdue, count: summary.count_overdue, color: '#f5222d', view: 'overdue' },
                ].map(function(card) {
                    return (
                        <SummaryCard key={card.label} style={{ flex: 1, cursor: card.view ? 'pointer' : 'default', borderColor: activeView === card.view ? card.color : '#e8e8e8' }}
                            onClick={card.view ? function() { setActiveView(activeView === card.view ? 'all' : card.view); } : undefined}>
                            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>{card.label}</div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: card.color }}>R$ {toBRL(card.value || 0)}</div>
                            <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 2 }}>{card.count || 0} registro(s)</div>
                        </SummaryCard>
                    );
                })}
            </div>

            <TableList
                data={filtered}
                isLoading={isLoading}
                canEdit={canEdit}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onMarkAsReceived={handleMarkAsReceived}
            />

            <DrawerCreate
                visible={showDrawer}
                onClose={function() { setShowDrawer(false); setEditingRecord(null); }}
                record={editingRecord}
                onSuccess={handleSaveSuccess}
                customers={customers}
            />
        </div>
    );
};

export default ContasReceberList;
